// Retry para o carregamento de chunks de import dinâmico (next/dynamic, React.lazy).
//
// O WebKit/Safari aborta intermitentemente o fetch de um chunk JS que, na segunda tentativa,
// carrega normalmente (o arquivo existe e responde 200). Sem retry essa falha transiente sobe
// como ChunkLoadError — na maioria dos casos um erro unhandled nos primeiros segundos da página,
// sem derrubá-la. Reexecutar o `import()` algumas vezes com backoff resolve a grande maioria.
//
// Esta é a camada de PREVENÇÃO. A RECUPERAÇÃO (quando o chunk realmente sumiu do CDN após um
// deploy que invalidou o hash) é o reload guardado nos error boundaries. Quando o retry se esgota
// e o erro ainda é de chunk, fazemos aqui o mesmo reload guardado — compartilhando a chave e a
// janela de guarda com os boundaries, para que um único reload aconteça e nunca em loop.

type ImportFactory<T> = () => Promise<T>;

export interface RetryDynamicImportOptions {
  // Número de novas tentativas após a primeira falha (total de tentativas = retries + 1).
  retries?: number;
  // Atraso base do backoff exponencial (ms): baseDelayMs * 2^tentativa.
  baseDelayMs?: number;
}

// Injeção para teste — não faz parte da API pública.
export interface RetryDynamicImportDeps {
  delay?: (ms: number) => Promise<void>;
  reload?: () => void;
  storage?: Pick<Storage, 'getItem' | 'setItem'> | null;
  now?: () => number;
}

// Mesma chave e janela do guard de reload dos error boundaries: um único reload resolve o chunk
// ausente (o HTML novo referencia os hashes novos); se o erro reincidir dentro da janela, o reload
// não resolveu — paramos de recarregar para não entrar em loop.
const RELOAD_GUARD_KEY = 'dd_chunk_reload_at';
const RELOAD_GUARD_WINDOW_MS = 10_000;

// A mensagem de falha de chunk varia por engine; só reload em erro reconhecidamente de chunk,
// para não recarregar em cima de um erro de avaliação do próprio módulo.
const CHUNK_ERROR_PATTERNS: readonly RegExp[] = [
  /loading chunk [\w-]+ failed/i,
  /failed to load chunk/i,
  /importing a module script failed/i,
  /error loading dynamically imported module/i,
  /^'?load failed'?$/i,
];

function isChunkLoadError(error: unknown): boolean {
  if (error == null || typeof error !== 'object') return false;
  const err = error as { name?: unknown; message?: unknown };
  if (err.name === 'ChunkLoadError') return true;
  const message = typeof err.message === 'string' ? err.message : '';
  return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(message.trim()));
}

function safeSessionStorage(): Pick<Storage, 'getItem' | 'setItem'> | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

// Decide se é seguro recarregar agora e registra o instante. false quando houve reload recente ou
// quando não há storage confiável para a guarda (ex.: Safari privado) — recarregar sem guarda
// arriscaria loop.
function canReloadForChunkError(storage: Pick<Storage, 'getItem' | 'setItem'> | null, now: number): boolean {
  if (!storage) return false;
  try {
    const last = Number(storage.getItem(RELOAD_GUARD_KEY)) || 0;
    if (last > 0 && now - last < RELOAD_GUARD_WINDOW_MS) return false;
    storage.setItem(RELOAD_GUARD_KEY, String(now));
    return true;
  } catch {
    return false;
  }
}

export function retryDynamicImport<T>(
  factory: ImportFactory<T>,
  options?: RetryDynamicImportOptions,
  deps?: RetryDynamicImportDeps
): ImportFactory<T> {
  const retries = options?.retries ?? 2;
  const baseDelayMs = options?.baseDelayMs ?? 300;
  const delay = deps?.delay ?? ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)));

  return async () => {
    let lastError: unknown;
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        return await factory();
      } catch (error) {
        lastError = error;
        if (attempt < retries) {
          await delay(baseDelayMs * 2 ** attempt);
        }
      }
    }

    const storage = deps?.storage !== undefined ? deps.storage : safeSessionStorage();
    const now = deps?.now ?? (() => Date.now());

    if (isChunkLoadError(lastError) && canReloadForChunkError(storage, now())) {
      const reload = deps?.reload ?? (() => window.location.reload());
      reload();
      // A navegação está a caminho; manter o pending (loading fallback) em vez de piscar um erro.
      return new Promise<T>(() => {});
    }

    throw lastError;
  };
}
